import React from 'react';

import { createStyles } from '../tss';

type VMessage = string;

interface ValidationMessagesApi {
  setError: (id: string, error: VMessage) => void;
  removeError: (id: string) => void;
  getErrors: () => VMessage[];
}

const useStyles = createStyles(({ theme }) => ({
  errorMessage: {
    color: 'var(--colors-text-error)',
    fontSize: 'var(--font-sizes-sm)',
  },
  hstack: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  content: {
    width: '100%',
  },
  messages: {
    width: '100%',
  },
}));

const ValidationMessagesContext = React.createContext<ValidationMessagesApi | undefined>(undefined);

const ValidationMessages: React.FC = () => {
  const { classes } = useStyles();
  const context = React.useContext(ValidationMessagesContext);
  // istanbul ignore if -- @preserve
  if (context === undefined) {
    // istanbul ignore next -- @preserve
    throw new Error('useValidationMessages cannot be called outside ValidationMessagesProvider');
  }
  const errors = [...new Set(context.getErrors())];
  return (
    <div>
      {errors.map((error) => (
        <div className={classes.errorMessage} key={error}>
          {String(error)}
        </div>
      ))}
    </div>
  );
};

interface Props {
  className?: string;
  classes?: Partial<ReturnType<typeof useStyles>['classes']>;
  children: React.ReactNode;
  hstack?: boolean;
}

export const ValidationContainer: React.FC<Props> = ({ className, classes: ovrClasses, children, hstack }) => {
  const { classes, cx } = useStyles({ classesOverrides: ovrClasses });
  const errorsRef = React.useRef<Map<string, VMessage>>(new Map());
  const [errorsStr, setErrorsStr] = React.useState('');
  return (
    <ValidationMessagesContext.Provider
      value={React.useMemo(() => ({
        setError: (id: string, error: VMessage) => {
          errorsRef.current.set(id, error);
          setErrorsStr([...errorsRef.current.values()].join(''));
        },
        removeError: (id: string) => {
          errorsRef.current.delete(id);
          setErrorsStr([...errorsRef.current.values()].join(''));
        },
        getErrors: () => [...errorsRef.current.values()],
      }), [errorsStr])}
    >
      <div className={className}>
        <div className={cx(classes.content, { [classes.hstack]: hstack })}>
          {children}
        </div>
        <div className={classes.messages}>
          <ValidationMessages />
        </div>
      </div>
    </ValidationMessagesContext.Provider>
  );
};

export const useSupplyErrorMessage = (error?: VMessage): boolean => {
  const idRef = React.useRef<string>(String(Math.random()));
  const context = React.useContext(ValidationMessagesContext);
  React.useEffect(() => {
    if (context !== undefined && error !== undefined) {
      const id = idRef.current;
      context.setError(id, error);
      return () => context.removeError(id);
    }
    return undefined;
  }, [context, error]);
  return !!context;
};
